import { cloneElement } from 'react'
import { fmtTime } from '@/shared/lib/format'
import { cls } from '@/ui/cls'
import { Ico } from '@/ui/Ico'
import { Eyebrow } from '@/ui'
import type { LeadRow } from '@/shared/types'
import * as m from 'motion/react-m'

interface Props {
  lead: LeadRow
  onJump: () => void
  onDismiss: () => void
}

// Top-right banner that pops when a `is_callback_alert` lead reaches its
// committed callback moment (RADAR-SE-022). Persists until the BDR jumps to
// the lead or dismisses — this isn't a "saved" toast, it's a "do something
// now" reminder. Dismiss is local-session only; the lead is still in the
// focus queue regardless so the callback can't be silently missed.
export default function CallbackToast({ lead, onJump, onDismiss }: Props) {
  const closeIcon = cloneElement(Ico.x, { width: 11, height: 11 })
  const clockIcon = cloneElement(Ico.clock, { width: 14, height: 14 })

  const promisedAt = lead.nextContactAt ? fmtTime(lead.nextContactAt) : ''

  return (
    <m.div
      className={cls(
        // Anchored bottom-right, above Focus's outcome bar (~134px tall),
        // rather than top-right: top-4 sat on the topbar's buttons, and
        // top-24 (clearing the topbar) still landed on the activity panel's
        // own header and close button, which sits in that same corner
        // whenever the panel is open alongside Focus.
        'fixed bottom-40 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)]',
        'bg-white border border-brand shadow-lg rounded-[3px]',
        'p-3 flex flex-col gap-2',
      )}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8, scale: 0.98 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="grid place-items-center w-7 h-7 rounded-full bg-brand text-ink-1000 shrink-0">
            {clockIcon}
          </span>
          <div className="min-w-0">
            <Eyebrow size="sm" gap="none">
              Dags för återuppringning
            </Eyebrow>
            <div className="text-[13px] font-semibold text-ink-1000 truncate" title={lead.company}>
              {lead.company}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Avvisa"
          title="Avvisa"
          className="shrink-0 bg-transparent border border-ink-200 w-6 h-6 rounded grid place-items-center text-ink-500 hover:text-ink-1000 hover:border-ink-1000 cursor-pointer p-0"
        >
          {closeIcon}
        </button>
      </header>

      <div className="text-[11px] text-ink-500">
        {lead.contact ? <>Utlovat {lead.contact}{promisedAt && ` · ${promisedAt}`}</> : <>Utlovat{promisedAt && ` · ${promisedAt}`}</>}
      </div>

      <div className="flex items-center justify-end gap-2 mt-1">
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] text-ink-500 hover:text-ink-1000 bg-transparent border-0 cursor-pointer p-1"
        >
          Avvisa
        </button>
        <button
          type="button"
          onClick={onJump}
          className="text-[11px] font-semibold text-ink-1000 bg-brand border border-brand rounded-[2px] cursor-pointer px-2.5 py-1 hover:bg-brand-deep hover:border-brand-deep"
        >
          Hoppa till leaden
        </button>
      </div>
    </m.div>
  )
}
